import React from "react";
import { cn } from "@/lib/utils";

/**
 * LoadingState — panel-level loading indicator primitive.
 *
 * Differs from a spinner icon: this fills a container while its
 * data resolves. Renders either a set of skeleton bars (default)
 * or a small centered spinner (variant="spinner").
 *
 * Guardrails:
 *   • No pulsing color glow.
 *   • Skeleton bars use `--ds-color-surface-inset` with an opacity
 *     tween — NOT a shimmering gradient sweep.
 *   • Spinner rotates via CSS but honours `prefers-reduced-motion`
 *     (durations in the token layer become 0ms).
 *
 * A11y: `role="status"` + visually-hidden text so AT users hear
 * "Loading, please wait" while the animation runs.
 */

function SkeletonRows({ rows = 3 }) {
  return (
    <div className="space-y-2 w-full max-w-md" aria-hidden>
      {Array.from({ length: rows }).map((_, i) => (
        <div
          key={i}
          className="h-3 rounded-[var(--ds-radius-sm)] bg-[var(--ds-color-surface-inset)]"
          style={{
            width: `${100 - i * 12}%`,
            opacity: 0.7,
          }}
        />
      ))}
    </div>
  );
}

function Spinner() {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      aria-hidden
      style={{
        animation: `ds-spin var(--ds-duration-slow) var(--ds-easing-linear) infinite`,
      }}
    >
      <circle
        cx="12"
        cy="12"
        r="9"
        fill="none"
        stroke="var(--ds-color-surface-inset)"
        strokeWidth="3"
      />
      <path
        d="M12 3 A9 9 0 0 1 21 12"
        fill="none"
        stroke="var(--ds-color-accent)"
        strokeWidth="3"
        strokeLinecap="round"
      />
      <style>{`@keyframes ds-spin { to { transform: rotate(360deg); } }`}</style>
    </svg>
  );
}

export function LoadingState({
  variant = "skeleton",
  label = "Loading",
  rows = 3,
  className,
  "data-testid": testId,
}) {
  return (
    <div
      role="status"
      aria-live="polite"
      data-testid={testId}
      className={cn(
        "flex flex-col items-center justify-center py-8",
        className
      )}
    >
      <span className="sr-only">{label}</span>
      {variant === "spinner" ? <Spinner /> : <SkeletonRows rows={rows} />}
    </div>
  );
}

export default LoadingState;
